import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Trash2,
  Building2,
  ImageIcon,
  AlertCircle,
  CheckCircle,
  Camera,
  Sparkles,
  Info,
  Copy,
  Check,
  Pencil,
  Save,
  X,
  Palette,
  LayoutGrid,
  Share2,
  Instagram,
  Facebook,
  Globe,
  MessageCircle,
  Grid3X3,
  List,
  Eye,
  Clock,
  AlertTriangle,
  Grid2X2
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { API_ENDPOINTS, getAssetUrl } from '../../config/api';
import { api } from '../../lib/api';
import { MENU_THEMES, THEME_LIST } from '../../config/menuThemes';
import type { MenuThemeId, MenuViewMode } from '../../types';

// TikTok icon component (not in lucide)
const TikTokIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

interface Restaurant {
  id: string;
  name: string;
  logoUrl: string | null;
  // Menu Customization
  menuTheme?: MenuThemeId;
  defaultViewMode?: MenuViewMode;
  showProductBadges?: boolean;
  showPreparationTime?: boolean;
  showAllergens?: boolean;
  // Social Links
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  websiteUrl?: string;
  whatsappNumber?: string;
}

export default function SettingsPage() {
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Menu customization states
  const [selectedTheme, setSelectedTheme] = useState<MenuThemeId>('classic');
  const [viewMode, setViewMode] = useState<MenuViewMode>('grid3');
  const [showBadges, setShowBadges] = useState(true);
  const [showPrepTime, setShowPrepTime] = useState(false);
  const [showAllergens, setShowAllergens] = useState(true);

  // Social links states
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [website, setWebsite] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  const [savingSettings, setSavingSettings] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);

  useEffect(() => {
    loadRestaurant();
  }, [user?.restaurantId]);

  const loadRestaurant = async () => {
    if (!user?.restaurantId) {
      console.error('No restaurantId available');
      setError('Impossibile caricare i dati del ristorante');
      return;
    }

    try {
      const response = await api.get(API_ENDPOINTS.restaurant.byId(user.restaurantId));
      const data = response.data;
      setRestaurant(data);

      // Populate customization states - validate theme exists
      const themeId = data.menuTheme as MenuThemeId;
      setSelectedTheme(MENU_THEMES[themeId] ? themeId : 'classic');
      setViewMode(data.defaultViewMode || 'grid3');
      setShowBadges(data.showProductBadges ?? true);
      setShowPrepTime(data.showPreparationTime ?? false);
      setShowAllergens(data.showAllergens ?? true);

      // Populate social links
      setInstagram(data.instagramUrl || '');
      setFacebook(data.facebookUrl || '');
      setTiktok(data.tiktokUrl || '');
      setWebsite(data.websiteUrl || '');
      setWhatsapp(data.whatsappNumber || '');
    } catch (err) {
      console.error('Failed to load restaurant', err);
      setError('Errore durante il caricamento dei dati del ristorante');
    }
  };

  const handleSaveMenuSettings = async () => {
    if (!user?.restaurantId) return;

    setSavingSettings(true);
    setError(null);

    try {
      const response = await api.put(API_ENDPOINTS.restaurant.byId(user.restaurantId), {
        menuTheme: selectedTheme,
        defaultViewMode: viewMode,
        showProductBadges: showBadges,
        showPreparationTime: showPrepTime,
        showAllergens: showAllergens,
        instagramUrl: instagram || null,
        facebookUrl: facebook || null,
        tiktokUrl: tiktok || null,
        websiteUrl: website || null,
        whatsappNumber: whatsapp || null,
      });
      setRestaurant(response.data);
      setSuccess('Impostazioni salvate con successo!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save settings', err);
      setError('Errore durante il salvataggio delle impostazioni');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = async (file: File) => {
    if (!user?.restaurantId) {
      setError('ID ristorante non disponibile');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Solo file immagine sono consentiti');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Il file non può superare i 5MB');
      return;
    }

    setError(null);
    setSuccess(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          setRestaurant(data);
          setSuccess('Logo caricato con successo!');
          setUploadProgress(100);
        } else {
          setError('Errore durante il caricamento del logo');
        }
        setUploading(false);
      });

      xhr.addEventListener('error', () => {
        setError('Errore durante il caricamento del logo');
        setUploading(false);
      });

      xhr.open('POST', API_ENDPOINTS.restaurant.logo(user.restaurantId));
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
      xhr.send(formData);
    } catch (err) {
      setError('Errore durante il caricamento del logo');
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm('Sei sicuro di voler eliminare il logo?')) return;

    if (!user?.restaurantId) {
      setError('ID ristorante non disponibile');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const response = await api.delete(API_ENDPOINTS.restaurant.logo(user.restaurantId));
      setRestaurant(response.data);
      setSuccess('Logo eliminato con successo!');
    } catch (err) {
      setError('Errore durante l\'eliminazione del logo');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEditingName = () => {
    setNewName(restaurant?.name || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 100);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNewName('');
  };

  const handleSaveName = async () => {
    if (!user?.restaurantId || !newName.trim()) {
      setError('Il nome non può essere vuoto');
      return;
    }

    if (newName.trim() === restaurant?.name) {
      setEditingName(false);
      return;
    }

    setSavingName(true);
    setError(null);

    try {
      const response = await api.put(API_ENDPOINTS.restaurant.byId(user.restaurantId), {
        name: newName.trim()
      });
      setRestaurant(response.data);
      setSuccess('Nome aggiornato con successo!');
      setEditingName(false);

      // Aggiorna anche lo store dell'utente se il nome del ristorante è memorizzato lì
      if (updateUser && user) {
        updateUser({ ...user, restaurantName: newName.trim() });
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update restaurant name', err);
      setError('Errore durante l\'aggiornamento del nome');
    } finally {
      setSavingName(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      cancelEditingName();
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Impostazioni
            </h1>
            <p className="text-gray-500 dark:text-white/50 text-sm">
              Personalizza il tuo ristorante
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        {/* Left Column - Logo Upload */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
            {/* Card Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-orange-500/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                  <ImageIcon size={20} className="text-orange-500 dark:text-orange-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Logo del Ristorante</h2>
                  <p className="text-xs text-gray-500 dark:text-white/40">
                    Visibile ai clienti nel menu digitale
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Logo Preview & Upload Area */}
              <div className="flex flex-col md:flex-row gap-6">
                {/* Current Logo / Upload Zone */}
                <div
                  className={`relative flex-shrink-0 ${dragActive ? 'scale-105' : ''} transition-transform duration-200`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {restaurant?.logoUrl ? (
                    <div className="relative group">
                      <div className="w-44 h-44 rounded-2xl overflow-hidden border-2 border-gray-200 dark:border-white/[0.1] bg-gray-50 dark:bg-white/[0.02] shadow-lg">
                        <img
                          src={getAssetUrl(restaurant.logoUrl)}
                          alt={`Logo di ${restaurant.name}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={handleFileSelect}
                          className="p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                          title="Cambia logo"
                        >
                          <Camera size={20} className="text-white" />
                        </button>
                        <button
                          onClick={handleDeleteLogo}
                          className="p-3 bg-red-500/80 hover:bg-red-500 rounded-xl transition-colors"
                          title="Elimina logo"
                        >
                          <Trash2 size={20} className="text-white" />
                        </button>
                      </div>
                      {/* Badge */}
                      <div className="absolute -top-2 -right-2 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-lg shadow-lg">
                        Attivo
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleFileSelect}
                      disabled={uploading}
                      className={`w-44 h-44 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center transition-all duration-200
                        ${dragActive
                          ? 'border-orange-500 bg-orange-500/10 scale-105'
                          : 'border-gray-300 dark:border-white/20 hover:border-orange-500 dark:hover:border-orange-500/50 hover:bg-orange-500/5'
                        }
                        ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mb-3">
                        <Upload size={24} className="text-gray-400 dark:text-white/40" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-white/70">Carica Logo</p>
                      <p className="text-xs text-gray-500 dark:text-white/40 mt-1">o trascina qui</p>
                    </button>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                {/* Upload Info & Actions */}
                <div className="flex-1 space-y-4">
                  {/* Upload Progress */}
                  <AnimatePresence>
                    {uploading && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-white/60">Caricamento in corso...</span>
                          <span className="font-medium text-orange-500">{Math.round(uploadProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-white/[0.08] rounded-full h-2 overflow-hidden">
                          <motion.div
                            className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Messages */}
                  <AnimatePresence mode="wait">
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex items-start gap-3"
                      >
                        <AlertCircle size={18} className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                      </motion.div>
                    )}

                    {success && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl flex items-start gap-3"
                      >
                        <CheckCircle size={18} className="text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-emerald-700 dark:text-emerald-400">{success}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Requirements */}
                  <div className="p-4 bg-gray-50 dark:bg-white/[0.02] rounded-xl border border-gray-100 dark:border-white/[0.06]">
                    <div className="flex items-center gap-2 mb-3">
                      <Info size={14} className="text-gray-400 dark:text-white/40" />
                      <p className="text-sm font-medium text-gray-700 dark:text-white/70">Specifiche consigliate</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-xs text-gray-600 dark:text-white/50">JPG, PNG, WebP</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-xs text-gray-600 dark:text-white/50">Max 5MB</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-xs text-gray-600 dark:text-white/50">512x512px</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-xs text-gray-600 dark:text-white/50">Rapporto 1:1</span>
                      </div>
                    </div>
                  </div>

                  {/* Upload Button (shown only when logo exists) */}
                  {restaurant?.logoUrl && (
                    <button
                      onClick={handleFileSelect}
                      disabled={uploading}
                      className="w-full px-5 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/25"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Caricamento...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          Carica Nuovo Logo
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Restaurant Info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Restaurant Card */}
          {restaurant && (
            <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-blue-500/5 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                    <Building2 size={20} className="text-blue-500 dark:text-blue-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Info Ristorante</h2>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Nome Ristorante
                  </label>
                  <AnimatePresence mode="wait">
                    {editingName ? (
                      <motion.div
                        key="editing"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="mt-2"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            ref={nameInputRef}
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={handleNameKeyDown}
                            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.1] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                            placeholder="Nome del ristorante"
                            disabled={savingName}
                          />
                          <button
                            onClick={handleSaveName}
                            disabled={savingName || !newName.trim()}
                            className="p-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                            title="Salva"
                          >
                            {savingName ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Save size={16} />
                            )}
                          </button>
                          <button
                            onClick={cancelEditingName}
                            disabled={savingName}
                            className="p-2 bg-gray-200 dark:bg-white/[0.1] hover:bg-gray-300 dark:hover:bg-white/[0.15] text-gray-600 dark:text-white/60 rounded-lg transition-colors"
                            title="Annulla"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-white/40">
                          Premi Enter per salvare, Esc per annullare
                        </p>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="display"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="mt-1 flex items-center gap-2 group"
                      >
                        <p className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                          {restaurant.name}
                        </p>
                        <button
                          onClick={startEditingName}
                          className="p-2 opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-all"
                          title="Modifica nome"
                        >
                          <Pencil size={14} className="text-gray-400 dark:text-white/40" />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* ID */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    ID Ristorante
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-white/[0.04] rounded-lg font-mono text-sm text-gray-700 dark:text-white/70 truncate">
                      {restaurant.id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(restaurant.id)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                      title="Copia ID"
                    >
                      {copied ? (
                        <Check size={16} className="text-emerald-500" />
                      ) : (
                        <Copy size={16} className="text-gray-400 dark:text-white/40" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-lg shadow-orange-500/25">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Tutto Pronto!</h3>
                <p className="text-sm text-white/70">Il tuo ristorante è configurato</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-white/80" />
                <span className="text-white/90">Menu digitale attivo</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-white/80" />
                <span className="text-white/90">QR Code disponibile</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-white/80" />
                <span className="text-white/90">Ordini in tempo reale</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Menu Customization Section */}
      <div className="mt-8 max-w-6xl space-y-6">
        {/* Theme Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-purple-500/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                  <Palette size={20} className="text-purple-500 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tema Menu</h2>
                  <p className="text-xs text-gray-500 dark:text-white/40">
                    Scegli lo stile visivo del menu per i clienti
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {THEME_LIST.map((theme) => {
                  const isSelected = selectedTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => setSelectedTheme(theme.id)}
                      className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 ${
                        isSelected
                          ? 'border-orange-500 ring-4 ring-orange-500/20 scale-[1.02]'
                          : 'border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.15] hover:scale-[1.01]'
                      }`}
                    >
                      {/* Mini Preview Background */}
                      <div
                        className="h-32 relative overflow-hidden"
                        style={{ background: theme.heroGradient }}
                      >
                        {/* Decorative elements based on theme */}
                        {theme.hasParticles && (
                          <div className="absolute inset-0 opacity-40">
                            {[...Array(8)].map((_, i) => (
                              <div
                                key={i}
                                className="absolute w-1 h-1 rounded-full animate-pulse"
                                style={{
                                  backgroundColor: theme.primaryColor,
                                  left: `${15 + i * 10}%`,
                                  top: `${20 + (i % 3) * 25}%`,
                                  animationDelay: `${i * 0.2}s`
                                }}
                              />
                            ))}
                          </div>
                        )}

                        {/* Pattern overlay */}
                        {theme.backgroundPattern === 'grid' && (
                          <div
                            className="absolute inset-0"
                            style={{
                              backgroundImage: `linear-gradient(${theme.primaryColor}15 1px, transparent 1px), linear-gradient(90deg, ${theme.primaryColor}15 1px, transparent 1px)`,
                              backgroundSize: '20px 20px',
                            }}
                          />
                        )}

                        {/* Mock card */}
                        <div
                          className="absolute bottom-3 left-3 right-3 rounded-lg p-2 flex items-center gap-2 backdrop-blur-sm"
                          style={{
                            backgroundColor: theme.cardBackground,
                            border: `1px solid ${theme.borderColor}`,
                            borderRadius: theme.cardBorderRadius
                          }}
                        >
                          <div
                            className="w-8 h-8 rounded-md flex items-center justify-center text-base"
                            style={{ backgroundColor: `${theme.primaryColor}30` }}
                          >
                            {theme.emoji}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className="h-2 w-16 rounded-full mb-1"
                              style={{ backgroundColor: theme.textColor }}
                            />
                            <div
                              className="h-1.5 w-12 rounded-full opacity-50"
                              style={{ backgroundColor: theme.mutedColor }}
                            />
                          </div>
                          <div
                            className="text-[10px] font-bold"
                            style={{ color: theme.primaryColor }}
                          >
                            €12
                          </div>
                        </div>

                        {/* Glow effect */}
                        <div
                          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full blur-2xl opacity-40"
                          style={{ backgroundColor: theme.primaryColor }}
                        />
                      </div>

                      {/* Theme info */}
                      <div className="p-3 bg-white dark:bg-[#0e0e12]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                              <span>{theme.emoji}</span>
                              {theme.name}
                            </p>
                            <p className="text-[10px] text-gray-500 dark:text-white/40">
                              {theme.description}
                            </p>
                          </div>
                          {/* Color dots */}
                          <div className="flex -space-x-1">
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white dark:border-[#0e0e12]"
                              style={{ backgroundColor: theme.primaryColor }}
                            />
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white dark:border-[#0e0e12]"
                              style={{ backgroundColor: theme.secondaryColor }}
                            />
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white dark:border-[#0e0e12]"
                              style={{ backgroundColor: theme.accentColor }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Selected indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Large Theme Preview */}
              <div className="mt-6">
                <p className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Eye size={14} />
                  Anteprima Cliente
                </p>
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: MENU_THEMES[selectedTheme].backgroundColor,
                    border: `1px solid ${MENU_THEMES[selectedTheme].borderColor}`
                  }}
                >
                  {/* Hero section preview */}
                  <div
                    className="p-6 relative overflow-hidden"
                    style={{ background: MENU_THEMES[selectedTheme].heroGradient }}
                  >
                    {/* Decorative glow */}
                    <div
                      className="absolute top-0 left-1/4 w-32 h-32 rounded-full blur-3xl opacity-30"
                      style={{ backgroundColor: MENU_THEMES[selectedTheme].primaryColor }}
                    />
                    <div
                      className="absolute bottom-0 right-1/4 w-24 h-24 rounded-full blur-2xl opacity-20"
                      style={{ backgroundColor: MENU_THEMES[selectedTheme].secondaryColor }}
                    />

                    <div className="relative text-center">
                      <div
                        className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-3xl mb-3"
                        style={{
                          background: MENU_THEMES[selectedTheme].buttonGradient,
                          boxShadow: `0 10px 30px ${MENU_THEMES[selectedTheme].shadowColor}`
                        }}
                      >
                        {MENU_THEMES[selectedTheme].emoji}
                      </div>
                      <h3
                        className="text-lg font-bold"
                        style={{
                          color: MENU_THEMES[selectedTheme].textColor,
                          textShadow: MENU_THEMES[selectedTheme].cardStyle === 'neon'
                            ? `0 0 20px ${MENU_THEMES[selectedTheme].primaryColor}`
                            : 'none'
                        }}
                      >
                        {restaurant?.name || 'Il Tuo Ristorante'}
                      </h3>
                      <p style={{ color: MENU_THEMES[selectedTheme].mutedColor }} className="text-sm">
                        Scopri il nostro menu
                      </p>
                    </div>
                  </div>

                  {/* Categories preview */}
                  <div className="px-4 py-3 flex gap-2 justify-center flex-wrap" style={{ borderBottom: `1px solid ${MENU_THEMES[selectedTheme].borderColor}` }}>
                    {['Antipasti', 'Primi', 'Secondi'].map((cat, i) => (
                      <div
                        key={cat}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: i === 0 ? MENU_THEMES[selectedTheme].buttonGradient : MENU_THEMES[selectedTheme].cardBackground,
                          color: i === 0 ? '#fff' : MENU_THEMES[selectedTheme].mutedColor,
                          boxShadow: i === 0 ? `0 2px 10px ${MENU_THEMES[selectedTheme].shadowColor}` : 'none',
                        }}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>

                  {/* Products preview */}
                  <div className="p-4 grid grid-cols-2 gap-3">
                    {[
                      { name: 'Bruschetta al Pomodoro', price: '8.50', emoji: '🍅' },
                      { name: 'Spaghetti Carbonara', price: '14.00', emoji: '🍝' },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="rounded-xl overflow-hidden transition-all"
                        style={{
                          backgroundColor: MENU_THEMES[selectedTheme].cardBackground,
                          border: `1px solid ${MENU_THEMES[selectedTheme].borderColor}`,
                          borderRadius: MENU_THEMES[selectedTheme].cardBorderRadius
                        }}
                      >
                        <div
                          className="h-20 flex items-center justify-center text-3xl"
                          style={{
                            background: `linear-gradient(135deg, ${MENU_THEMES[selectedTheme].cardBackgroundHover}, ${MENU_THEMES[selectedTheme].cardBackground})`
                          }}
                        >
                          {item.emoji}
                        </div>
                        <div className="p-2">
                          <p
                            className="text-xs font-medium truncate"
                            style={{ color: MENU_THEMES[selectedTheme].textColor }}
                          >
                            {item.name}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span
                              className="text-[10px]"
                              style={{ color: MENU_THEMES[selectedTheme].mutedColor }}
                            >
                              Dettagli →
                            </span>
                            <span
                              className="text-xs font-bold"
                              style={{ color: MENU_THEMES[selectedTheme].primaryColor }}
                            >
                              €{item.price}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Cart button preview */}
                  <div className="p-4 pt-0">
                    <div
                      className="rounded-xl p-3 flex items-center justify-between text-white text-sm font-medium"
                      style={{
                        background: MENU_THEMES[selectedTheme].buttonGradient,
                        boxShadow: `0 10px 30px ${MENU_THEMES[selectedTheme].shadowColor}`
                      }}
                    >
                      <span>🛒 Vedi carrello</span>
                      <span className="font-bold">€22.50</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Layout Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-cyan-500/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center">
                  <LayoutGrid size={20} className="text-cyan-500 dark:text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Layout Menu</h2>
                  <p className="text-xs text-gray-500 dark:text-white/40">
                    Configura come i prodotti vengono visualizzati
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* View Mode */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-white/70 block mb-3">
                  Vista Predefinita
                </label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { id: 'grid3' as MenuViewMode, label: 'Griglia 3', icon: Grid3X3 },
                    { id: 'grid4' as MenuViewMode, label: 'Griglia 4', icon: Grid2X2 },
                    { id: 'list' as MenuViewMode, label: 'Lista', icon: List },
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setViewMode(mode.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all ${
                        viewMode === mode.id
                          ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                          : 'border-gray-200 dark:border-white/[0.08] text-gray-600 dark:text-white/60 hover:border-gray-300 dark:hover:border-white/[0.15]'
                      }`}
                    >
                      <mode.icon size={18} />
                      <span className="text-sm font-medium">{mode.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Show Badges */}
                <label className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-white/[0.08] cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                      <Eye size={16} className="text-amber-500 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Badge Prodotti</p>
                      <p className="text-xs text-gray-500 dark:text-white/40">Popular, New, Chef</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={showBadges}
                    onChange={(e) => setShowBadges(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </label>

                {/* Show Prep Time */}
                <label className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-white/[0.08] cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                      <Clock size={16} className="text-blue-500 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Tempo Prep.</p>
                      <p className="text-xs text-gray-500 dark:text-white/40">Minuti stimati</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={showPrepTime}
                    onChange={(e) => setShowPrepTime(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </label>

                {/* Show Allergens */}
                <label className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-white/[0.08] cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle size={16} className="text-red-500 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Allergeni</p>
                      <p className="text-xs text-gray-500 dark:text-white/40">Info allergie</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={showAllergens}
                    onChange={(e) => setShowAllergens(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-pink-500/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-pink-100 dark:bg-pink-500/20 flex items-center justify-center">
                  <Share2 size={20} className="text-pink-500 dark:text-pink-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Social & Contatti</h2>
                  <p className="text-xs text-gray-500 dark:text-white/40">
                    Link visibili nel menu cliente
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Instagram */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider flex items-center gap-2 mb-2">
                    <Instagram size={14} className="text-pink-500" />
                    Instagram
                  </label>
                  <input
                    type="text"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="@ristorantetest"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>

                {/* Facebook */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider flex items-center gap-2 mb-2">
                    <Facebook size={14} className="text-blue-600" />
                    Facebook
                  </label>
                  <input
                    type="text"
                    value={facebook}
                    onChange={(e) => setFacebook(e.target.value)}
                    placeholder="facebook.com/ristorante"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>

                {/* TikTok */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider flex items-center gap-2 mb-2">
                    <TikTokIcon size={14} className="text-gray-900 dark:text-white" />
                    TikTok
                  </label>
                  <input
                    type="text"
                    value={tiktok}
                    onChange={(e) => setTiktok(e.target.value)}
                    placeholder="@ristorantetiktok"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>

                {/* Website */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider flex items-center gap-2 mb-2">
                    <Globe size={14} className="text-emerald-500" />
                    Sito Web
                  </label>
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://mioristorante.it"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>

                {/* WhatsApp */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wider flex items-center gap-2 mb-2">
                    <MessageCircle size={14} className="text-green-500" />
                    WhatsApp
                  </label>
                  <input
                    type="text"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+39 333 1234567"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex justify-end"
        >
          <button
            onClick={handleSaveMenuSettings}
            disabled={savingSettings}
            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/25"
          >
            {savingSettings ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save size={18} />
                Salva Impostazioni Menu
              </>
            )}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
